import React from "react";
import { Plus, X } from "lucide-react";

// Character profile is organised by main SECTIONS (Update 08, revised).
// Each section is a single toggle. When enabled, it provides one multi-line
// text area. The `hint` lists the kind of detail that belongs in that section.
export const PROFILE_SECTIONS = [
  { key: "identity", label: "Identity", hint: "Character name & role title, department / faction, story importance (main, supporting or background character)." },
  { key: "background", label: "Background", hint: "Who they are, their history, purpose, and relationship to the attraction's story (2–3 paragraphs)." },
  { key: "costume", label: "Costume", hint: "Clothing, footwear, hair, makeup, props, accessories, colour palette, special effects / lighting elements." },
  { key: "character_behaviour", label: "Character Behaviour", hint: "Personality, demeanour, and movement style — how they carry themselves and interact with the environment." },
  { key: "voice_speech", label: "Voice & Speech", hint: "Voice type, speaking speed, volume, accent, and typical speech patterns or catchphrases." },
  { key: "performance", label: "Performance", hint: "Example dialogue lines, primary/secondary/signature scare style, and practical scare techniques." },
];

export const hasProfileContent = (role) =>
  (role?.profile_fields || []).length > 0 || (role?.custom_fields || []).length > 0;

const newId = () => Math.random().toString(36).slice(2, 10);

// ---------------------------------------------------------------------------
// Builder (used in the Add/Edit Role form)
// ---------------------------------------------------------------------------
export function CharacterProfileBuilder({ profileFields = [], profileValues = {}, customFields = [], onChange }) {
  const emit = (patch) => onChange({ profileFields, profileValues, customFields, ...patch });

  const toggleSection = (key, enabled) => {
    if (enabled) emit({ profileFields: [...profileFields, key] });
    else {
      const { [key]: _removed, ...rest } = profileValues;
      emit({ profileFields: profileFields.filter((k) => k !== key), profileValues: rest });
    }
  };
  const setValue = (key, value) => emit({ profileValues: { ...profileValues, [key]: value } });

  const addCustom = () => emit({ customFields: [...customFields, { id: newId(), label: "", value: "" }] });
  const updateCustom = (id, patch) => emit({ customFields: customFields.map((c) => (c.id === id ? { ...c, ...patch } : c)) });
  const removeCustom = (id) => emit({ customFields: customFields.filter((c) => c.id !== id) });

  return (
    <div className="space-y-3" data-testid="character-profile-builder">
      <p className="text-xs text-[#6B6862]">Tick the sections you want in this character profile. Each enabled section gives you a free-text area to describe it. Only enabled sections appear on the role and in the actor's portal.</p>
      {PROFILE_SECTIONS.map((s) => {
        const enabled = profileFields.includes(s.key);
        return (
          <div key={s.key} className="border border-[#1a1a1a] bg-[#070707]">
            <label className="flex items-start gap-2.5 px-3 py-2.5 cursor-pointer">
              <input type="checkbox" data-testid={`profile-toggle-${s.key}`} checked={enabled} onChange={(e) => toggleSection(s.key, e.target.checked)} className="w-4 h-4 mt-0.5 accent-[#D91616]" />
              <span>
                <span className={`font-display text-sm uppercase tracking-wider ${enabled ? "text-[#F2EBE1]" : "text-[#8a857d]"}`}>{s.label}</span>
                <span className="block text-xs text-[#6B6862] mt-0.5">{s.hint}</span>
              </span>
            </label>
            {enabled && (
              <div className="px-3 pb-3">
                <textarea data-testid={`profile-value-${s.key}`} rows={4} value={profileValues[s.key] || ""} onChange={(e) => setValue(s.key, e.target.value)}
                  placeholder={s.hint}
                  className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-blood resize-none" />
              </div>
            )}
          </div>
        );
      })}

      {/* Custom sections */}
      <div className="border border-[#1a1a1a] bg-[#070707]">
        <div className="px-3 py-2 label-mini border-b border-[#1a1a1a] bg-[#0d0d0d]">Custom Sections</div>
        <div className="p-3 space-y-3">
          {customFields.length === 0 && <p className="text-xs text-[#6B6862]">No custom sections. Add one for anything not covered above.</p>}
          {customFields.map((c) => (
            <div key={c.id} className="flex gap-2 items-start" data-testid={`custom-field-${c.id}`}>
              <input value={c.label} onChange={(e) => updateCustom(c.id, { label: e.target.value })} placeholder="Section label"
                className="w-1/3 bg-[#050505] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-blood" data-testid={`custom-label-${c.id}`} />
              <textarea value={c.value} onChange={(e) => updateCustom(c.id, { value: e.target.value })} placeholder="Content" rows={2}
                className="flex-1 bg-[#050505] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-blood resize-none" data-testid={`custom-value-${c.id}`} />
              <button type="button" onClick={() => removeCustom(c.id)} className="p-2 text-[#6B6862] hover:text-blood" data-testid={`custom-remove-${c.id}`}><X size={14} /></button>
            </div>
          ))}
          <button type="button" onClick={addCustom} data-testid="add-custom-field-btn" className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-blood hover:text-[#FF3333]"><Plus size={13} /> Add Custom Section</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Display (admin role detail + actor portal)
// ---------------------------------------------------------------------------
export function CharacterProfileDisplay({ role }) {
  const fields = role?.profile_fields || [];
  const values = role?.profile_values || {};
  const customs = (role?.custom_fields || []).filter((c) => (c.label || "").trim() || (c.value || "").trim());
  const sections = PROFILE_SECTIONS.filter((s) => fields.includes(s.key));

  if (sections.length === 0 && customs.length === 0) {
    return <p className="text-sm text-[#6B6862] italic">No character profile built yet.</p>;
  }

  const Block = ({ label, value }) => (
    <div>
      <h4 className="font-display text-sm uppercase tracking-wider text-blood mb-1.5">{label}</h4>
      <p className="text-sm text-[#A19D94] whitespace-pre-wrap leading-relaxed">{value || "—"}</p>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="character-profile-display">
      {sections.map((s) => <Block key={s.key} label={s.label} value={values[s.key]} />)}
      {customs.map((c) => <Block key={c.id || c.label} label={c.label || "Untitled"} value={c.value} />)}
    </div>
  );
}
